<template>
  <div class="app-drawer-list">
    <v-list v-if="dictionariesLoaded">
      <div
        v-for="item in routes.filter(isVisible)"
        :key="item.title"
      >
        <v-list-item
          v-if="isVisibleItem(item)"
          :to="resolvePath(onlyOneChild.path)"
          :class="item.alias ? ($route.path == item.alias[0] ? 'v-list-item--active' : '') : ''"
          ripple="ripple"
        >
          <v-list-item-icon class="app-drawer-list__icon">
            <v-icon>{{ getListIcon(onlyOneChild) }}</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title>
              <span v-html="getListTitle(onlyOneChild)"></span>
              <v-badge
                class="mt-0"
                v-if="getListBadge(onlyOneChild)"
                :content="getListBadge(onlyOneChild)"
                :value="getListBadge(onlyOneChild)"
                color="primary"
                inline
              />
            </v-list-item-title>
          </v-list-item-content>
          <div class="v-list-item__after">&nbsp;</div>
        </v-list-item>

        <v-list-group v-else :prepend-icon="getListIcon(item)">
          <template v-slot:activator>
            <v-list-item-content>
              <v-list-item-title>
                {{ getListTitle(item) }}
              </v-list-item-title>
            </v-list-item-content>
          </template>

          <app-drawer-list
            :routes="item.children"
            :base-path="resolvePath(item.path)"
          />
        </v-list-group>
      </div>
    </v-list>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { resolve } from "path";
import Config from '@/config.json'

export default {
  name: "AppDrawerList",
  props: {
    isNest: Boolean,
    routes: {
      type: Array,
      default: () => {}
    },
    basePath: {
      type: String,
      default: ""
    }
  },
  data() {
    this.onlyOneChild = null;
    return {};
  },
  computed: {
    ...mapGetters('chatStates', [
      'allUnreadedMessages'
    ]),
    ...mapGetters('userStates', [
      'getRolename'
    ]),
    ...mapState('userStates', [
      'userFeatureFlags'
    ]),
    ...mapState('dictionariesStates', [
      'dictionariesLoaded',
      'dictionaryPracticeUserRole',
      'dictionaryBhUsersRoleIds',
      'dictionaryFeatureFlagState'
    ]),
  },
  methods: {
    isVisible(item) {
      // Check if features are enabled in config (env variables)
      if (item.enabledFeatures && Array.isArray(item.enabledFeatures)) {
        const featureDisabled = item.enabledFeatures.find(feature => (Config[feature] !== true && Config[feature] !== 'true'))

        // Found at least one disabled feature which is required
        if (featureDisabled) {
          return false
        }
      }

      // Check if item is default hidden in router config
      if (item.hidden) return false

      // Check if item should be hidden on specific user role
      let hideOnRole = true
      if (item.hideOnRole) {
        if (Array.isArray(item.hideOnRole)) {
          if (item.hideOnRole.includes(this.dictionaryPracticeUserRole[this.getRolename])) hideOnRole = false
        } else {
          if (item.hideOnRole == this.dictionaryPracticeUserRole[this.getRolename]) hideOnRole = false
        }
        if (item.hideOnRoleAlsoAdmins) {
          if (Array.isArray(item.hideOnRole)) {
            if (item.hideOnRole.includes(this.dictionaryBhUsersRoleIds[this.getRolename])) hideOnRole = false
          } else {
            if (item.hideOnRole == this.dictionaryBhUsersRoleIds[this.getRolename]) hideOnRole = false
          }
        }
      }
      if (hideOnRole === false) return false

      // Check if available feature flag is other than inactive in jwt token
      if (item.availableFeatureFlag) {
        if (this.userFeatureFlags[item.availableFeatureFlag] === this.dictionaryFeatureFlagState[this.dictionaryFeatureFlagState.Inactive]) return false
      }

      // Show element
      return true
    },
    isExternal(path) {
      return /^(https?:|mailto:|tel:)/.test(path);
    },
    isVisibleItem(item) {
      return (
        this.hasOneVisibleChild(item.children, item) &&
        (!this.onlyOneChild.children || this.onlyOneChild.noVisibleChildren) &&
        !item.alwaysShow
      );
    },
    hasOneVisibleChild(children = [], parent) {
      const visibleChildren = children.filter(item => {
        if (item.hidden) return false;
        // Temp set(will be used if only has one visible child)
        this.onlyOneChild = item;
        return true;
      });

      if (visibleChildren.length === 1) {
        this.onlyOneChild.path = resolve(parent.path, this.onlyOneChild.path);
        this.onlyOneChild.meta.icon =
          this.onlyOneChild.meta.icon || parent.meta.icon || "";

        return true;
      }

      if (visibleChildren.length === 0) {
        this.onlyOneChild = { ...parent, noVisibleChildren: true };
        return true;
      }

      return false;
    },
    resolvePath(path) {
      if (this.isExternal(path)) {
        return path;
      }
      return resolve(this.basePath, path);
    },
    getListIcon(item) {
      return item.meta && item.meta.icon ? item.meta.icon : " ";
    },
    getListTitle(item) {
      return item.meta && item.meta.menuTitle
        ? item.meta.menuTitle
        : item.meta && item.meta.title
        ? item.meta.title
        : "";
    },
    getListBadge (item) {
      return item.name === 'Chat'
        ? this.allUnreadedMessages
        : ''
    }
  }
};
</script>
<style lang="scss">
@import "@/styles/variables.scss";

.app-drawer-list {
  padding-bottom: 0px;
  padding-top: 32px;

  .v-list {
    &-item {
      &::before {
        background-color: $bh-blue--4;
        border-top-left-radius: 24px;
        border-bottom-left-radius: 24px;
        left: 16px;
        z-index: 2;
        transition: none;
      }

      &:not(.v-list-item--active):hover {
        z-index: 3;

        &::before {
          opacity: 0.3 !important;
        }
      }

      .v-ripple__container {
        border-top-left-radius: 24px;
        border-bottom-left-radius: 24px;
        left: 16px;
      }

      &--active {
        color: $bh-blue !important;

        &::before {
          opacity: 1 !important;
        }

        &::after {
          background-color: #213665;
        }

        .v-ripple__container {
          display: none !important;
        }

        .v-list-item__after {
          opacity: 1;
        }
      }

      &__after {
        position: absolute;
        right: 0;
        top: -24px;
        height: calc(100% + 48px);
        width: 24px;
        opacity: 0;
        background-color: $bh-blue--4;
        // transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
        z-index: 1;

        &::before,
        &::after {
          content: " ";
          position: absolute;
          right: 0;
          width: 24px;
          height: 24px;
          background-color: var(--main-color);
          border-radius: 0;
        }

        &::before {
          top: 0;
          border-bottom-right-radius: 24px;
        }

        &::after {
          bottom: 0;
          border-top-right-radius: 24px;
        }
      }

      &__icon {
        width: 16px;
        margin: 0 12px 0 16px !important;
        height: 48px;
        text-align: center;
        z-index: 2;
        min-width: 16px;
        align-self: center;
      }

      &__title {
        font-weight: 700;
        line-height: 1.425 !important;
        z-index: 2;
        text-overflow: clip;
        white-space: normal;
      }
    }
  }
}
</style>
